<template>
  <EditorShell v-bind="{ config, lotteryId, loaded, navigateToHome, optionsReady }">
    <template v-if="config">
      <EditorPlayList v-bind="{ animated, config, store }">
        <template #default="{ play }">
          <component :is="picker" v-bind="{ config, lotteryId, play }" />
        </template>
      </EditorPlayList>

      <EditorDeleteAllPlays :visible="canDeleteAllPlays" @confirm="deleteAllPlays" />
    </template>

    <template #control-panel>
      <EditorControlPanel v-bind="{ actions, disabled }" @click="handleAction" />
    </template>
  </EditorShell>
</template>

<script lang="ts" setup>
import type { ControlPanelAction } from '../@types/editor'

import {
  EDITOR_CONTROL_PANEL_ACTION_ID,
  EDITOR_QUICK_PICK_ACTION_ID_PREFIX,
} from '../constants/editor'
import {
  NUMBER_LOTTERY_DEFAULT_PICKER,
  NUMBER_LOTTERY_PICKER_BY_LOTTERY,
} from '../constants/numberLotteryPicker'
import { useNumberLotteryStore } from '../stores/numberLottery'

const SINGLE_QUICK_PICK = 1

const { t } = useI18n()

const { config, editorIsReady, lotteryId, navigateToHome, optionsReady, playsReady, store } =
  useEditor(useNumberLotteryStore)
const { addLine, addLineDisabled, disabled, scrollToCenteredPlay } = useEditorControlPanel(
  store,
  config,
)
const { applyQuickPickFromQuery, quickPickReady } = useEditorQuickPickQuery(store, config)
const { animated, canDeleteAllPlays, deleteAllPlays } = useDeleteAllPlays(store, config)
const { triggerBatch } = useEditorQuickPickAnimation()

useEditorMaxPlaysToast(store, config, disabled)

const picker = NUMBER_LOTTERY_PICKER_BY_LOTTERY[lotteryId] ?? NUMBER_LOTTERY_DEFAULT_PICKER

const loaded = computed(() => !!config.value && playsReady.value && quickPickReady.value)

const actions = computed<ControlPanelAction[]>(() => [
  {
    disabled: addLineDisabled.value,
    icon: 'plus',
    id: EDITOR_CONTROL_PANEL_ACTION_ID.AddLine,
    label: t('editor.controlpanel.button.addline'),
    variant: 'fade',
  },
  ...quickPickActions.value,
])

const isFillTarget = computed(() => {
  const currentConfig = config.value
  return !!currentConfig && store.isQuickPickFillTarget(currentConfig)
})

const quickPickActions = computed<ControlPanelAction[]>(() =>
  quickPickQuantities.value.map((quantity) => ({
    ariaLabel: t('editor.controlpanel.button.quickpickmultiple.arialabel', quantity),
    icon: quantity === SINGLE_QUICK_PICK && isFillTarget.value ? 'flash' : 'flash-plus',
    id: `${EDITOR_QUICK_PICK_ACTION_ID_PREFIX}${quantity}`,
    label:
      quantity === SINGLE_QUICK_PICK ? t('editor.controlpanel.button.quickpick') : String(quantity),
    variant: 'prominent',
  })),
)

const quickPickQuantities = computed(() => {
  const validPlays = config.value?.quickPick?.validQuickPickPlays ?? []
  return [...new Set([validPlays.at(0) ?? 1, validPlays.at(-1) ?? 1])]
})

const handleAction = async (actionId: string) => {
  const currentConfig = config.value
  if (!currentConfig) return

  if (actionId === EDITOR_CONTROL_PANEL_ACTION_ID.AddLine) return addLine()

  const quantity = toQuickPickQuantity(actionId)
  if (!quantity) return

  const playIds = triggerBatch({
    config: currentConfig,
    expandedPlayId: store.expandedPlayId,
    plays: () => store.plays,
    quickPick: () => store.quickPickPlays(currentConfig, quantity),
  })

  await scrollToCenteredPlay(playIds.at(-1) ?? null)
}

const toQuickPickQuantity = (actionId: string): Maybe<number> => {
  if (!actionId.startsWith(EDITOR_QUICK_PICK_ACTION_ID_PREFIX)) return null
  const quantity = Number(actionId.slice(EDITOR_QUICK_PICK_ACTION_ID_PREFIX.length))
  return Number.isInteger(quantity) && quantity > 0 ? quantity : null
}

onMounted(async () => {
  await editorIsReady
  await applyQuickPickFromQuery()
})
</script>
